import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, CalendarDays, Clock, CheckCircle2,
  AlertTriangle, Download, Send, XCircle, ChevronLeft, ChevronRight,
  RefreshCw, Scale, FileText, Trash2, Plus, MapPin, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { FASES_ORDEN, getUnidadCodigo } from './constants';

const DIAS_SEMANA = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const ESTADO_COLORS = {
  confirmada: { bg: '#dcfce7', border: '#86efac', text: '#166534', dot: '#22c55e' },
  por_confirmar: { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', dot: '#f97316' },
  tentativa: { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', dot: '#3b82f6' },
};

const ESTADOS_PROGRAMACION_FRONTEND = [
  'programacion_pendiente', 'esperando_confirmacion',
  'programacion_vencida', 'reprogramacion_solicitada',
];

const EntregaProgramacion = () => {
  const navigate = useNavigate();
  const { id: entregaId } = useParams();
  const [entrega, setEntrega] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [proximasEntregas, setProximasEntregas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mesActual, setMesActual] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const prevArqRef = useRef(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFecha, setModalFecha] = useState('');
  const [modalHoraInicio, setModalHoraInicio] = useState('09:00');
  const [modalHoraFin, setModalHoraFin] = useState('11:00');
  const [modalNota, setModalNota] = useState('');
  const [saving, setSaving] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [loadingCal, setLoadingCal] = useState(false);

  const [citModalOpen, setCitModalOpen] = useState(false);
  const [citForm, setCitForm] = useState({ notaria_nombre: '', notaria_direccion: '', fecha_citacion: '', numero_acta: '', notas: '' });
  const [reenviando, setReenviando] = useState(false);
  const [dayModalData, setDayModalData] = useState(null);
  useEffect(() => {
    if (!dayModalData) return;
    const onKey = (e) => { if (e.key === 'Escape') setDayModalData(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dayModalData]);

  // Confirmación manual
  const [confManualOpen, setConfManualOpen] = useState(false);
  const [confManualWarning, setConfManualWarning] = useState(false);
  const [confManualData, setConfManualData] = useState({ fecha: '', hora_inicio: '09:00', hora_fin: '11:00' });
  const [confManualInvitados, setConfManualInvitados] = useState([]);
  const [confManualSaving, setConfManualSaving] = useState(false);
  const [confManualMaxInv, setConfManualMaxInv] = useState(1);
  const [confManualLoading, setConfManualLoading] = useState(false);

  const loadEntrega = useCallback(async () => {
    for (let intento = 1; intento <= 2; intento++) {
      try {
        const resp = await api.get(`/entregas/${entregaId}`);
        setEntrega(resp.data);
        return resp.data;
      } catch (err) {
        if (err.response?.status === 404) {
          toast.error('Entrega no encontrada');
          navigate('/entregas');
          return null;
        }
        if (intento === 1) {
          await new Promise(r => setTimeout(r, 800));
          continue;
        }
        toast.error('No se pudo cargar la entrega', {
          action: { label: 'Reintentar', onClick: () => window.location.reload() },
        });
      }
    }
    return null;
  }, [entregaId, navigate]);

  const [errorCarga, setErrorCarga] = useState(false);

  // GET con un reintento ante error temporal; si vuelve a fallar se avisa (no se muestra vacío en silencio)
  const getConReintento = useCallback(async (url) => {
    try {
      return await api.get(url);
    } catch (err) {
      if (err.response?.status && err.response.status < 500) throw err;
      await new Promise(r => setTimeout(r, 800));
      return api.get(url);
    }
  }, []);

  const loadCalendario = useCallback(async (arqId, yr, mo) => {
    if (!arqId) return;
    setLoadingCal(true);
    const mesStr = `${yr}-${String(mo + 1).padStart(2, '0')}`;
    try {
      const resp = await getConReintento(`/entregas/programaciones/vista-calendario?arquitecto_id=${arqId}&mes=${mesStr}`);
      setEventos(resp.data || []);
    } catch {
      setErrorCarga(true);
    }
    setLoadingCal(false);
  }, [getConReintento]);

  const loadProximas = useCallback(async (arqId) => {
    if (!arqId) return [];
    try {
      const resp = await getConReintento(`/entregas/programaciones/proximas-entregas?arquitecto_id=${arqId}`);
      const data = resp.data || [];
      setProximasEntregas(data);
      return data;
    } catch {
      setErrorCarga(true);
      return [];
    }
  }, [getConReintento]);

  useEffect(() => {
    (async () => {
      const ent = await loadEntrega();
      if (!ent?.arquitecto_id) { setLoading(false); return; }

      const sameArq = prevArqRef.current === ent.arquitecto_id;
      prevArqRef.current = ent.arquitecto_id;

      if (sameArq) {
        setLoading(false);
        return;
      }

      // Próximas entregas y fechas de ESTA entrega en paralelo
      const now = new Date();
      let targetMonth = { year: now.getFullYear(), month: now.getMonth() };
      const [, fechasResp] = await Promise.all([
        loadProximas(ent.arquitecto_id),
        getConReintento(`/entregas/${entregaId}/programacion-fechas`).catch(() => null),
      ]);

      try {
        const fechasEntrega = fechasResp?.data || [];
        if (fechasEntrega.length > 0) {
          const primeraFecha = fechasEntrega[0].fecha; // "YYYY-MM-DD"
          if (primeraFecha) {
            const [y, m] = primeraFecha.split('-').map(Number);
            targetMonth = { year: y, month: m - 1 };
          }
        }
      } catch { /* sin fechas, queda mes actual */ }

      // Load calendar once for the correct month, then update state
      await loadCalendario(ent.arquitecto_id, targetMonth.year, targetMonth.month);
      skipNextCalRef.current = true; // prevent second useEffect from double-loading
      setMesActual(targetMonth);
      setLoading(false);
    })();
  }, [entregaId]); // eslint-disable-line

  // Re-fetch calendar only on manual month navigation
  const skipNextCalRef = useRef(true);
  useEffect(() => {
    if (skipNextCalRef.current) {
      skipNextCalRef.current = false;
      return;
    }
    if (entrega?.arquitecto_id) {
      loadCalendario(entrega.arquitecto_id, mesActual.year, mesActual.month);
    }
  }, [mesActual]); // eslint-disable-line

  const reload = async () => {
    const ent = await loadEntrega();
    if (ent?.arquitecto_id) {
      await Promise.all([
        loadCalendario(ent.arquitecto_id, mesActual.year, mesActual.month),
        loadProximas(ent.arquitecto_id),
      ]);
    }
  };

  // Calendar helpers
  const diasDelMes = useMemo(() => {
    const first = new Date(mesActual.year, mesActual.month, 1);
    const lastDay = new Date(mesActual.year, mesActual.month + 1, 0).getDate();
    let startDay = first.getDay() - 1; // Monday=0
    if (startDay < 0) startDay = 6;

    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [mesActual]);

  const eventosPorDia = useMemo(() => {
    const map = {};
    eventos.forEach(ev => {
      const d = ev.fecha; // "YYYY-MM-DD"
      if (!map[d]) map[d] = [];
      map[d].push(ev);
    });
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => (a.hora_inicio || '').localeCompare(b.hora_inicio || ''))
    );
    return map;
  }, [eventos]);

  const tentativasActuales = useMemo(() => {
    return eventos.filter(e => e.entrega_id === entregaId && e.estado === 'tentativa');
  }, [eventos, entregaId]);

  const puedeAgregar = entrega && ['programacion_pendiente', 'programacion_vencida', 'reprogramacion_solicitada'].includes(entrega.estado) && tentativasActuales.length < 3;
  const puedeEnviar = entrega && ['programacion_pendiente', 'programacion_vencida', 'reprogramacion_solicitada'].includes(entrega.estado) && tentativasActuales.length > 0;

  const handleDayClick = (day) => {
    if (!day || !puedeAgregar) return;
    const fecha = `${mesActual.year}-${String(mesActual.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setModalFecha(fecha);
    setModalHoraInicio('09:00');
    setModalHoraFin('11:00');
    setModalNota('');
    setModalOpen(true);
  };

  const handleAgregarFecha = async () => {
    if (!modalHoraInicio || !modalHoraFin) { toast.error('Complete las horas'); return; }
    setSaving(true);
    try {
      await api.post(`/entregas/${entregaId}/programacion-fechas`, {
        fecha: modalFecha,
        hora_inicio: modalHoraInicio,
        hora_fin: modalHoraFin,
        nota: modalNota || undefined,
      });
      toast.success('Fecha tentativa agregada');
      setModalOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const handleEliminarFecha = async (fechaId) => {
    try {
      await api.delete(`/entregas/programacion-fechas/${fechaId}`);
      toast.info('Fecha eliminada');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleEnviarPropuesta = async () => {
    setEnviando(true);
    try {
      await api.post(`/entregas/${entregaId}/enviar-propuesta`);
      toast.success('Propuesta enviada al cliente');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setEnviando(false); }
  };

  const handleCitacion = async () => {
    if (!citForm.notaria_nombre || !citForm.fecha_citacion) { toast.error('Complete los campos obligatorios'); return; }
    setSaving(true);
    try {
      await api.post(`/entregas/${entregaId}/citacion-notarial`, citForm);
      toast.success('Citación notarial creada');
      setCitModalOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const navMes = (delta) => {
    setMesActual(prev => {
      let m = prev.month + delta;
      let y = prev.year;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      return { year: y, month: m };
    });
  };

  // ─── Confirmación manual ───
  const handleOpenConfManual = async () => {
    setConfManualLoading(true);
    try {
      const params = { proyecto_id: entrega.proyecto_id };
      if (entrega.subdivision_id) params.subdivision_id = entrega.subdivision_id;
      const resp = await api.get('/entregas/config/mailing/resolve', { params });
      setConfManualMaxInv(resp.data?.max_invitados ?? 1);
    } catch {
      setConfManualMaxInv(1);
    } finally {
      setConfManualLoading(false);
    }

    if (entrega.estado === 'programacion_pendiente') {
      setConfManualWarning(true);
    } else {
      setConfManualOpen(true);
    }
  };

  const handleConfManualSubmit = async () => {
    if (!confManualData.fecha || !confManualData.hora_inicio || !confManualData.hora_fin) {
      toast.error('Complete la fecha y horario');
      return;
    }
    // Validar invitados no vacíos
    const invIncompletos = confManualInvitados.some(i => !i.nombre.trim() || !i.documento.trim());
    if (invIncompletos) {
      toast.error('Complete nombre y documento de todos los invitados o elimine los vacíos');
      return;
    }
    setConfManualSaving(true);
    try {
      const resp = await api.post(`/entregas/${entregaId}/confirmacion-manual`, {
        fecha: confManualData.fecha,
        hora_inicio: confManualData.hora_inicio,
        hora_fin: confManualData.hora_fin,
        invitados: confManualInvitados.filter(i => i.nombre.trim() && i.documento.trim()),
      });
      toast.success(resp.data.message);
      setConfManualOpen(false);
      setConfManualData({ fecha: '', hora_inicio: '09:00', hora_fin: '11:00' });
      setConfManualInvitados([]);
      loadEntrega();
      loadCalendario(entrega.arquitecto_id, mesActual.year, mesActual.month);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al confirmar manualmente');
    } finally {
      setConfManualSaving(false);
    }
  };

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const esMesActual = mesActual.year === today.getFullYear() && mesActual.month === today.getMonth();

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#5B7FA6]" /></div>;
  if (!entrega) return null;

  const estado = entrega.estado;
  const modoLectura = FASES_ORDEN.indexOf(entrega.fase) > FASES_ORDEN.indexOf('Programación');
  const esVencida = !modoLectura && estado === 'programacion_vencida';
  const esReprogramacion = !modoLectura && estado === 'reprogramacion_solicitada';

  return (
    <div className="space-y-4" data-testid="programacion-page">
      {errorCarga && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="programacion-error-carga">
          <span>No se pudo cargar la agenda del arquitecto (calendario o próximas entregas). La información mostrada puede estar incompleta.</span>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()} data-testid="btn-reintentar-carga">Reintentar</Button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/entregas/${entregaId}`)} data-testid="btn-volver-detalle">
            <ArrowLeft size={16} className="mr-1" /> Volver
          </Button>
          <div>
            <span className="font-mono text-lg font-bold text-[#5B7FA6]">{entrega.codigo}</span>
            <p className="text-xs text-[#888]">{entrega.proyecto_nombre} — {getUnidadCodigo(entrega)} — {entrega.cliente_nombre}</p>
          </div>
        </div>
        {estado === 'esperando_confirmacion' && (
          <Button
            size="sm"
            variant="outline"
            disabled={reenviando}
            className="text-xs border-[#5B7FA6] text-[#5B7FA6] hover:bg-[#5B7FA6]/10"
            onClick={async () => {
              setReenviando(true);
              try {
                const resp = await api.post(`/entregas/${entregaId}/programacion-reenviar`);
                toast.success(`Invitación reenviada (${resp.data.correos_enviados} correo${resp.data.correos_enviados > 1 ? 's' : ''})`);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Error al reenviar');
              } finally {
                setReenviando(false);
              }
            }}
            data-testid="btn-reenviar-invitacion"
          >
            {reenviando ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
            Reenviar Invitación
          </Button>
        )}
        {!modoLectura && ESTADOS_PROGRAMACION_FRONTEND.includes(estado) && (
          <Button
            size="sm"
            variant="outline"
            className="text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
            onClick={handleOpenConfManual}
            disabled={confManualLoading}
            data-testid="btn-confirmacion-manual"
          >
            {confManualLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <UserCheck size={14} className="mr-1" />}
            Confirmar manualmente
          </Button>
        )}
      </div>

      {/* Banner modo lectura */}
      {modoLectura && (
        <div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-lg" data-testid="banner-lectura-programacion">
          <Clock size={16} className="text-slate-500 shrink-0" />
          <p className="text-sm text-slate-600">
            <span className="font-medium">Vista histórica</span> — Fase de programación completada
            {entrega.fecha_confirmada && <span> · Cita confirmada: <strong>{entrega.fecha_confirmada}</strong></span>}
          </p>
        </div>
      )}

      {/* Banner vencida */}
      {esVencida && (
        <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <AlertTriangle size={16} className="text-orange-500 shrink-0" />
          <div className="flex-1 text-sm text-orange-700">
            El cliente no respondió. Intentos: <strong>{(entrega.intentos_reprogramacion || 0) + 1}/2</strong>.
            {(entrega.intentos_reprogramacion || 0) + 1 < 2 ? ' Proponga nuevas fechas o escale a citación notarial.' : ' Se agotaron los intentos. Proceda con citación notarial.'}
          </div>
          {(entrega.intentos_reprogramacion || 0) + 1 >= 2 && (
            <Button size="sm" onClick={() => setCitModalOpen(true)} className="bg-red-600 hover:bg-red-700 text-white text-xs shrink-0" data-testid="btn-citacion-notarial">
              <Scale size={12} className="mr-1" /> Citación Notarial
            </Button>
          )}
        </div>
      )}

      {/* Banner reprogramación solicitada */}
      {esReprogramacion && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="banner-reprogramacion">
          <RefreshCw size={16} className="text-blue-500 shrink-0" />
          <div className="flex-1 text-sm text-blue-700">
            El cliente ha solicitado nuevas fechas. Intentos: <strong>{(entrega.intentos_reprogramacion || 0) + 1}/2</strong>.
            {(entrega.intentos_reprogramacion || 0) + 1 < 2 ? ' Proponga nuevas opciones de fecha.' : ' Se agotaron los intentos. Proceda con citación notarial.'}
          </div>
          {(entrega.intentos_reprogramacion || 0) + 1 >= 2 && (
            <Button size="sm" onClick={() => setCitModalOpen(true)} className="bg-red-600 hover:bg-red-700 text-white text-xs shrink-0" data-testid="btn-citacion-notarial-reprog">
              <Scale size={12} className="mr-1" /> Citación Notarial
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar - 2/3 */}
        <div className="lg:col-span-2">
          <Card className="border border-[#E8E8E7]" data-testid="calendario-grid">
            <CardContent className="p-4">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navMes(-1)} data-testid="btn-mes-anterior">
                    <ChevronLeft size={16} />
                  </Button>
                  <h2 className="text-lg font-bold text-[#2D2D2D] min-w-[180px] text-center">
                    {MESES[mesActual.month]} {mesActual.year}
                  </h2>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navMes(1)} data-testid="btn-mes-siguiente">
                    <ChevronRight size={16} />
                  </Button>
                </div>
                {puedeEnviar && (
                  <Button onClick={handleEnviarPropuesta} disabled={enviando} className="bg-[#5B7FA6] hover:bg-[#4a6d92] text-white text-xs" data-testid="btn-enviar-propuesta">
                    {enviando ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                    Enviar propuesta al cliente ({tentativasActuales.length})
                  </Button>
                )}
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-px mb-px">
                {DIAS_SEMANA.map(d => (
                  <div key={d} className="text-center text-[11px] font-semibold text-[#888] py-2 bg-[#f8f8f8] rounded-t">{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="relative grid grid-cols-7 gap-px bg-[#e8e8e7]">
                {loadingCal && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]" data-testid="calendario-spinner">
                    <Loader2 size={24} className="animate-spin text-[#5B7FA6]" />
                  </div>
                )}
                {diasDelMes.map((day, idx) => {
                  if (day === null) return <div key={`e-${idx}`} className="bg-[#fafafa] min-h-[90px]" />;
                  const fechaStr = `${mesActual.year}-${String(mesActual.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const evsDia = eventosPorDia[fechaStr] || [];
                  const isToday = esMesActual && fechaStr === todayStr;
                  const canClick = puedeAgregar;

                  return (
                    <div
                      key={day}
                      className={`bg-white min-h-[90px] p-1 relative ${canClick ? 'cursor-pointer hover:bg-[#f0f7ff]' : ''} ${isToday ? 'ring-2 ring-[#5B7FA6] ring-inset' : ''}`}
                      onClick={() => canClick && handleDayClick(day)}
                      data-testid={`cal-day-${day}`}
                    >
                      <span className={`text-xs font-semibold ${isToday ? 'text-[#5B7FA6]' : 'text-[#555]'}`}>{day}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {evsDia.slice(0, 3).map(ev => {
                          const colors = ESTADO_COLORS[ev.estado] || ESTADO_COLORS.tentativa;
                          const esPropia = ev.entrega_id === entregaId;
                          return (
                            <div
                              key={ev.fecha_id}
                              className="flex items-center gap-0.5 group"
                              style={{
                                background: colors.bg,
                                borderLeft: `3px solid ${colors.dot}`,
                                borderRadius: '3px',
                                padding: '1px 4px',
                                outline: esPropia ? `2px solid ${colors.dot}` : 'none',
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                if (esPropia && ev.estado === 'tentativa') handleEliminarFecha(ev.fecha_id);
                              }}
                              title={`${ev.codigo}${ev.unidad_codigo ? ' · ' + ev.unidad_codigo : ''} · ${ev.cliente_nombre} (${ev.hora_inicio?.substring(0,5)}-${ev.hora_fin?.substring(0,5)})${ev.arquitecto_nombre ? ' — ' + ev.arquitecto_nombre : ''} ${esPropia ? '[Esta entrega]' : ''}\n${ev.proyecto_nombre || ''}${ev.subdivision_nombre ? ' · ' + ev.subdivision_nombre : ''}`}
                            >
                              <span className="text-[9px] font-medium truncate" style={{ color: colors.text }}>
                                {ev.codigo?.substring(0, 10)} · {ev.cliente_nombre}
                              </span>
                              {esPropia && ev.estado === 'tentativa' && (
                                <Trash2 size={8} className="text-red-400 opacity-0 group-hover:opacity-100 shrink-0 ml-auto" />
                              )}
                            </div>
                          );
                        })}
                        {evsDia.length > 3 && (
                          <button
                            className="text-[8px] text-[#5B7FA6] font-medium hover:underline cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); setDayModalData({ fecha: fechaStr, eventos: evsDia }); }}
                            data-testid={`cal-more-${day}`}
                          >
                            +{evsDia.length - 3} más
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-5 mt-3 pt-2 border-t border-[#E8E8E7]">
                {[
                  { label: 'Confirmada', color: '#22c55e' },
                  { label: 'Por confirmar', color: '#f97316' },
                  { label: 'Tentativa', color: '#3b82f6' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
                    <span className="text-[11px] text-[#888]">{l.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - 1/3 */}
        <div className="lg:col-span-1 space-y-3">
          <Card className="border border-[#E8E8E7]" data-testid="proximas-entregas">
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-bold text-[#2D2D2D] flex items-center gap-1.5">
                <CalendarDays size={14} className="text-[#5B7FA6]" /> Próximas entregas
              </CardTitle>
              <p className="text-[10px] text-[#888]">Ordenadas por fecha más cercana</p>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto">
              {proximasEntregas.length === 0 && (
                <p className="text-xs text-[#aaa] text-center py-4">Sin entregas en programación</p>
              )}
              {proximasEntregas
                .slice()
                .sort((a, b) => (b.id === entregaId) - (a.id === entregaId))
                .map(ent => {
                const esActual = ent.id === entregaId;
                const fecha = ent.fecha_proxima;
                const fechaDate = fecha ? new Date(fecha + (fecha.includes('T') ? '' : 'T12:00:00')) : null;
                const dia = fechaDate ? fechaDate.getDate() : '?';
                const mesStr = fechaDate ? MESES[fechaDate.getMonth()]?.substring(0, 3).toUpperCase() : '';

                let tipoBadge = { label: ent.estado?.replace(/_/g, ' '), bg: '#f3f4f6', color: '#6b7280' };
                if (ent.tipo_evento === 'confirmada' || ent.estado === 'fecha_confirmada') tipoBadge = { label: 'Confirmada', bg: '#dcfce7', color: '#166534' };
                else if (ent.tipo_evento === 'por_confirmar' || ent.estado === 'esperando_confirmacion') tipoBadge = { label: 'Por confirmar', bg: '#fff7ed', color: '#9a3412' };
                else if (ent.tipo_evento === 'tentativa') tipoBadge = { label: 'Tentativa', bg: '#eff6ff', color: '#1e40af' };
                else if (ent.estado === 'programacion_vencida') tipoBadge = { label: 'Vencida', bg: '#fef2f2', color: '#991b1b' };
                else if (ent.estado === 'reprogramacion_solicitada') tipoBadge = { label: 'Reprogramación', bg: '#eff6ff', color: '#1e40af' };

                return (
                  <div
                    key={ent.id}
                    className={`flex gap-3 p-2.5 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${esActual ? 'border-[#5B7FA6] bg-[#5B7FA6]/5' : 'border-[#E8E8E7]'}`}
                    onClick={() => { if (!esActual) navigate(`/entregas/${ent.id}/programacion`); }}
                    data-testid={`proxima-${ent.id}`}
                  >
                    <div className="shrink-0 w-11 h-12 rounded-lg bg-[#f5f0eb] flex flex-col items-center justify-center">
                      <span className="text-base font-bold text-[#8B7355] leading-none">{dia}</span>
                      <span className="text-[9px] font-semibold text-[#8B7355] uppercase">{mesStr}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[10px] font-semibold text-[#5A5A5A]">entrega</span>
                        <Badge className="text-[9px] px-1.5 py-0 h-4 font-medium border-0" style={{ backgroundColor: tipoBadge.bg, color: tipoBadge.color }}>
                          {tipoBadge.label}
                        </Badge>
                      </div>
                      <p className="text-xs font-semibold text-[#2D2D2D] truncate">{ent.codigo} · {ent.proyecto_nombre}</p>
                      {getUnidadCodigo(ent) && <p className="text-[11px] font-mono font-medium text-[#555] break-all" data-testid={`proxima-unidad-${ent.codigo}`}>{getUnidadCodigo(ent)}</p>}
                      <p className="text-[10px] text-[#888] truncate">{ent.cliente_nombre}</p>
                      {/* All active dates */}
                      {(ent.fechas_activas || []).length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {ent.fechas_activas.map(fa => {
                            const fc = ESTADO_COLORS[fa.estado] || ESTADO_COLORS.tentativa;
                            return (
                              <div key={fa.fecha_id} className="flex items-center gap-1.5 text-[10px]">
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: fc.dot }} />
                                <span className="text-[#5A5A5A] font-medium">{fa.fecha}</span>
                                <span className="text-[#888]">{fa.hora_inicio}-{fa.hora_fin}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {!(ent.fechas_activas || []).length && ent.hora_inicio && ent.hora_fin && (
                        <p className="text-[10px] text-[#5A5A5A] flex items-center gap-1">
                          <Clock size={9} className="text-[#888]" /> {ent.hora_inicio} - {ent.hora_fin}
                        </p>
                      )}
                      {/* Action button */}
                      {!modoLectura && ent.estado === 'fecha_confirmada' && (
                        <Button size="sm" className="mt-1.5 h-6 text-[10px] bg-[#5A5A5A] hover:bg-[#3D3D3D] text-white" onClick={e => {
                          e.stopPropagation();
                          api.post(`/entregas/${ent.id}/transicion`, { nuevo_estado: 'entrega_en_proceso' }).then(() => { toast.success('Entrega iniciada'); reload(); }).catch(() => toast.error('Error'));
                        }} data-testid={`btn-iniciar-${ent.id}`}>
                          Iniciar entrega
                        </Button>
                      )}
                      {ent.estado === 'esperando_confirmacion' && (
                        <span className="text-[10px] text-orange-600 mt-1 block">esperando confirmación</span>
                      )}
                      {ent.estado === 'programacion_pendiente' && ent.tentativas_count > 0 && (
                        <span className="text-[10px] text-blue-600 mt-1 block">{ent.tentativas_count} fecha(s) tentativa(s)</span>
                      )}
                      {ent.estado === 'programacion_pendiente' && !ent.tentativas_count && (
                        <span className="text-[10px] text-[#aaa] mt-1 block">sin fechas propuestas</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal: Agregar fecha tentativa */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="modal-agregar-fecha">
          <DialogHeader>
            <DialogTitle className="text-base">Nueva fecha para {entrega?.codigo}</DialogTitle>
            <p className="text-sm text-[#888]">
              {modalFecha && new Date(modalFecha + 'T12:00:00').toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            {modalFecha && (eventosPorDia[modalFecha] || []).length > 0 && (
              <div className="mt-1.5 space-y-1">
                <p className="text-xs text-orange-600 flex items-center gap-1">
                  <AlertTriangle size={12} /> Este día tiene {eventosPorDia[modalFecha].length} cita(s) del arquitecto:
                </p>
                <div className="space-y-0.5 pl-4">
                  {eventosPorDia[modalFecha].map(ev => (
                    <p key={ev.fecha_id} className="text-[10px] text-[#888]">
                      <span className="font-medium text-[#555]">{ev.hora_inicio?.substring(0,5)}-{ev.hora_fin?.substring(0,5)}</span> — {ev.codigo}{ev.unidad_codigo ? ' · ' + ev.unidad_codigo : ''} · {ev.cliente_nombre}{ev.arquitecto_nombre ? ' — ' + ev.arquitecto_nombre : ''}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#888]">Hora inicio *</Label>
                <Input type="time" value={modalHoraInicio} onChange={e => {
                  const hi = e.target.value;
                  setModalHoraInicio(hi);
                  if (hi) {
                    const [h, m] = hi.split(':').map(Number);
                    const hFin = Math.min(h + 2, 23);
                    setModalHoraFin(`${String(hFin).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
                  }
                }} className="h-9" data-testid="input-modal-hora-inicio" />
              </div>
              <div>
                <Label className="text-xs text-[#888]">Hora fin *</Label>
                <Input type="time" value={modalHoraFin} onChange={e => setModalHoraFin(e.target.value)} className="h-9" data-testid="input-modal-hora-fin" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#888]">Nota (opcional)</Label>
              <Textarea value={modalNota} onChange={e => setModalNota(e.target.value)} rows={2} className="text-sm" placeholder="Nota adicional..." data-testid="input-modal-nota" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} className="text-xs">Cancelar</Button>
            <Button onClick={handleAgregarFecha} disabled={saving} className="bg-[#5B7FA6] hover:bg-[#4a6d92] text-white text-xs" data-testid="btn-confirmar-tentativa">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Citación Notarial */}
      <Dialog open={citModalOpen} onOpenChange={setCitModalOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="modal-citacion">
          <DialogHeader>
            <DialogTitle className="text-base text-red-700">Citación Notarial</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#888]">Notaría *</Label>
                <Input className="h-8 text-sm" value={citForm.notaria_nombre} onChange={e => setCitForm(f => ({ ...f, notaria_nombre: e.target.value }))} data-testid="input-notaria-nombre" />
              </div>
              <div>
                <Label className="text-xs text-[#888]">Dirección</Label>
                <Input className="h-8 text-sm" value={citForm.notaria_direccion} onChange={e => setCitForm(f => ({ ...f, notaria_direccion: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs text-[#888]">Fecha Citación *</Label>
                <Input type="date" className="h-8 text-sm" value={citForm.fecha_citacion} onChange={e => setCitForm(f => ({ ...f, fecha_citacion: e.target.value }))} data-testid="input-fecha-citacion" />
              </div>
              <div>
                <Label className="text-xs text-[#888]">N° Acta</Label>
                <Input className="h-8 text-sm" value={citForm.numero_acta} onChange={e => setCitForm(f => ({ ...f, numero_acta: e.target.value }))} />
              </div>
            </div>
            <Textarea placeholder="Notas (opcional)" value={citForm.notas} onChange={e => setCitForm(f => ({ ...f, notas: e.target.value }))} rows={2} className="text-sm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCitModalOpen(false)} className="text-xs">Cancelar</Button>
            <Button onClick={handleCitacion} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="btn-crear-citacion">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Scale size={14} className="mr-1" />}
              Crear Citación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: eventos del día */}
      {dayModalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDayModalData(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="day-events-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E8E7]">
              <div className="flex items-center gap-2">
                <CalendarDays size={16} className="text-[#5B7FA6]" />
                <h3 className="text-sm font-semibold text-[#2D2D2D]">
                  {(() => {
                    const [y, m, d] = dayModalData.fecha.split('-');
                    return `${parseInt(d)} de ${MESES[parseInt(m) - 1]} ${y}`;
                  })()}
                </h3>
                <Badge variant="outline" className="text-[10px]">{dayModalData.eventos.length} eventos</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDayModalData(null)} className="h-7 w-7 p-0">
                <XCircle size={14} />
              </Button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-3 space-y-2">
              {dayModalData.eventos.map(ev => {
                const colors = ESTADO_COLORS[ev.estado] || ESTADO_COLORS.tentativa;
                const esPropia = ev.entrega_id === entregaId;
                return (
                  <div
                    key={ev.fecha_id}
                    className="rounded-lg p-3 border"
                    style={{
                      backgroundColor: colors.bg,
                      borderColor: colors.dot + '40',
                      outline: esPropia ? `2px solid ${colors.dot}` : 'none',
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold font-mono" style={{ color: colors.dot }}>
                        {ev.codigo}{ev.unidad_codigo && <span className="ml-1 font-normal text-[#888]" data-testid="unidad-codigo">· {ev.unidad_codigo}</span>} {esPropia ? '(Esta entrega)' : ''}
                      </span>
                      <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: colors.dot, color: '#fff' }}>
                        {ev.estado === 'confirmada' ? 'Confirmada' : ev.estado === 'por_confirmar' ? 'Por confirmar' : 'Tentativa'}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#2D2D2D] font-medium mt-1">{ev.cliente_nombre}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#666]">
                      <span className="flex items-center gap-0.5"><Clock size={9} /> {(ev.hora_inicio || '').substring(0,5)} - {(ev.hora_fin || '').substring(0,5)}</span>
                      {ev.arquitecto_nombre && <span className="flex items-center gap-0.5"><MapPin size={9} /> {ev.arquitecto_nombre}</span>}
                    </div>
                    <p className="text-[10px] text-[#888] mt-1">{ev.proyecto_nombre || ''}{ev.subdivision_nombre ? ' · ' + ev.subdivision_nombre : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Warning: programacion_pendiente → recomendar enviar correo */}
      <Dialog open={confManualWarning} onOpenChange={setConfManualWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle size={18} /> Confirmación directa
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#555] leading-relaxed">
            Se recomienda realizar el envío del correo de invitación en lugar de establecer una fecha de entrega directamente. <strong>¿Estás seguro que deseas continuar?</strong>
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setConfManualWarning(false)}>Cancelar</Button>
            <Button size="sm" className="text-xs bg-amber-500 hover:bg-amber-600 text-white" onClick={() => { setConfManualWarning(false); setConfManualOpen(true); }} data-testid="btn-warning-continuar">
              Sí, continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Confirmación manual */}
      <Dialog open={confManualOpen} onOpenChange={setConfManualOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck size={18} className="text-amber-600" /> Confirmar cita manualmente
            </DialogTitle>
            <p className="text-xs text-[#888] mt-1">{entrega.codigo} — {entrega.cliente_nombre}</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-[#888]">Fecha *</Label>
                <Input
                  type="date"
                  className="h-8 text-sm"
                  value={confManualData.fecha}
                  onChange={e => setConfManualData(d => ({ ...d, fecha: e.target.value }))}
                  data-testid="conf-manual-fecha"
                />
              </div>
              <div>
                <Label className="text-xs text-[#888]">Hora inicio *</Label>
                <Input
                  type="time"
                  className="h-8 text-sm"
                  value={confManualData.hora_inicio}
                  onChange={e => {
                    const hi = e.target.value;
                    setConfManualData(d => ({
                      ...d,
                      hora_inicio: hi,
                      hora_fin: hi ? (() => { const [h, m] = hi.split(':'); return `${String(Math.min(parseInt(h) + 2, 23)).padStart(2, '0')}:${m}`; })() : d.hora_fin,
                    }));
                  }}
                  data-testid="conf-manual-hora-inicio"
                />
              </div>
              <div>
                <Label className="text-xs text-[#888]">Hora fin *</Label>
                <Input
                  type="time"
                  className="h-8 text-sm"
                  value={confManualData.hora_fin}
                  onChange={e => setConfManualData(d => ({ ...d, hora_fin: e.target.value }))}
                  data-testid="conf-manual-hora-fin"
                />
              </div>
            </div>

            {/* Invitados */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-[#888]">
                  Invitados ({confManualInvitados.length}/{confManualMaxInv})
                </Label>
                {confManualInvitados.length < confManualMaxInv ? (
                  <Button
                    type="button" variant="ghost" size="sm" className="h-6 text-xs text-[#5B7FA6]"
                    onClick={() => setConfManualInvitados(prev => [...prev, { nombre: '', documento: '' }])}
                    data-testid="conf-manual-add-invitado"
                  >
                    <Plus size={12} className="mr-1" /> Agregar
                  </Button>
                ) : (
                  <span className="text-[10px] text-amber-500">Máximo {confManualMaxInv} invitado{confManualMaxInv !== 1 ? 's' : ''}</span>
                )}
              </div>
              {confManualInvitados.length > 0 && (
                <div className="space-y-2">
                  {confManualInvitados.map((inv, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        placeholder="Nombre"
                        className="h-8 text-sm flex-1"
                        value={inv.nombre}
                        onChange={e => {
                          const upd = [...confManualInvitados];
                          upd[idx] = { ...upd[idx], nombre: e.target.value };
                          setConfManualInvitados(upd);
                        }}
                      />
                      <Input
                        placeholder="Documento"
                        className="h-8 text-sm w-32"
                        value={inv.documento}
                        onChange={e => {
                          const upd = [...confManualInvitados];
                          upd[idx] = { ...upd[idx], documento: e.target.value };
                          setConfManualInvitados(upd);
                        }}
                      />
                      <Button
                        type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                        onClick={() => setConfManualInvitados(prev => prev.filter((_, i) => i !== idx))}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setConfManualOpen(false)}>Cancelar</Button>
            <Button
              size="sm"
              className="text-xs bg-amber-500 hover:bg-amber-600 text-white"
              disabled={confManualSaving || !confManualData.fecha || !confManualData.hora_inicio || !confManualData.hora_fin}
              onClick={handleConfManualSubmit}
              data-testid="btn-conf-manual-submit"
            >
              {confManualSaving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
              Confirmar cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntregaProgramacion;
